/* eslint-disable no-mixed-operators */
import { getMe } from "../../lib/graph";
import { ICustomerVisit, IFeatPermission, IUserPermissions, IUserProfile } from "./types";
import _ from "lodash";
import { getDepartmentFromDisplayName, getDivisionFromDepartment, removeAliasDomain } from "../../utils/converter";
import Constants from "../../core/libraries/Constants";
import Helper from "../../core/libraries/Helper";
import { FeberApiClient } from "../../services/commonServices";
import { checkChatPermission } from "../OpenAI/Chat/services";
import { ChatModes } from "../OpenAI/types";

export const getCurrentUserInfo = async (accessToken: string) => {
  const myProfile = {} as IUserProfile
  try {
    let me = await getMe();
    if (!!me) {
      myProfile.userToken = accessToken;
      myProfile.id = me.id;
      myProfile.email = me.mail;
      myProfile.loginName = me.userPrincipalName;
      myProfile.name = me.displayName;
      myProfile.department = getDepartmentFromDisplayName(me.displayName);
      myProfile.division = getDivisionFromDepartment(myProfile.department);
      const tokenParts = accessToken.split('.');
      const tokenPayload = JSON.parse(atob(tokenParts[1]));
      const groups = tokenPayload.groups;
      const roles: string[] = await getRoles(removeAliasDomain(myProfile.loginName));
      myProfile.roles = roles;
      !!groups ? myProfile.groups = groups : myProfile.groups = [];
      myProfile.permissions = await getMyPermissions(myProfile);
      myProfile.menuItems = !!myProfile.permissions && Helper.makeMenuItems(myProfile.permissions);

      try {
        const [hasAdvanced, hasStandard, hasPep] = await Promise.all([
          checkChatPermission(ChatModes.ADVANCED_CHAT).catch(() => false),
          checkChatPermission(ChatModes.STANDARD_CHAT).catch(() => false),
          checkChatPermission(ChatModes.PEP_CHAT).catch(() => false)
        ]);
        const menuFeatures: IFeatPermission[] = [
          { featureName: ChatModes.ADVANCED_CHAT, hasAccess: hasAdvanced === true },
          { featureName: ChatModes.STANDARD_CHAT, hasAccess: hasStandard === true },
          { featureName: ChatModes.PEP_CHAT, hasAccess: hasPep === true }
        ];
        myProfile.menuFeatures = menuFeatures;
      } catch (error) {
        console.error('Failed to check chat permissions:', error);
        myProfile.menuFeatures = [
          { featureName: ChatModes.ADVANCED_CHAT, hasAccess: false },
          { featureName: ChatModes.STANDARD_CHAT, hasAccess: false }
        ];
      }
      return myProfile
    }
    else return undefined
  } catch (error) {
    console.log(error);
    return undefined
  }
}

// LL, RnD division admins
// Report types admin (thesis, paper, ll)
// supper admin (highest permission )
export const getMyPermissions = async (userProfile: IUserProfile) => {
  let permissions: IUserPermissions = {
    checkHasPermission: () => {
      return false;
    },
    checkHasDivisionAdminPermission: () => {
      return false;
    },
  };

  let groups: string[] = userProfile.groups
  let roles: string[] = userProfile.roles
  permissions.isSuperAdmin = groups.some(group => group.toLowerCase() === Constants.IDM_GROUPS.SUPER_ADMIN.toLowerCase())
    || roles.some(role => role.toLowerCase() === Constants.IDM_GROUPS.SUPER_ADMIN.toLowerCase());
  permissions.isLLAdmin = groups.some(group => group.toLowerCase() === Constants.IDM_GROUPS.REPORT_TYPE_ADMIN.LL.toLowerCase())
    || roles.some(role => role.toLowerCase() === Constants.IDM_GROUPS.REPORT_TYPE_ADMIN.LL.toLowerCase());
  permissions.isThesisAdmin = groups.some(group => group.toLowerCase() === Constants.IDM_GROUPS.REPORT_TYPE_ADMIN.THESIS.toLowerCase())
    || roles.some(role => role.toLowerCase() === Constants.IDM_GROUPS.REPORT_TYPE_ADMIN.THESIS.toLowerCase());
  permissions.isPaperAdmin = groups.some(group => group.toLowerCase() === Constants.IDM_GROUPS.REPORT_TYPE_ADMIN.PAPER.toLowerCase())
    || roles.some(role => role.toLowerCase() === Constants.IDM_GROUPS.REPORT_TYPE_ADMIN.PAPER.toLowerCase());
  permissions.isRnDUser = groups.some(group => group.toLowerCase() === Constants.IDM_GROUPS.RND_USER.toLowerCase())
    || roles.some(role => role.toLowerCase() === Constants.IDM_GROUPS.RND_USER.toLowerCase());
  permissions.isConfigurationUser = groups.some(group => group.toLowerCase() === Constants.IDM_GROUPS.CONFIGURATION.toLowerCase())
    || roles.some(role => role.toLowerCase() === Constants.IDM_GROUPS.CONFIGURATION.toLowerCase());

  let llDivAdmin = groups.find(group =>
    !!group && group.toLowerCase().includes("ll_admin")
    && group.split('_').length === 5)
    || roles.find(role => role.toLowerCase().includes('ll_admin') && role.split('_').length === 5)
  let rndDivAdmin = groups.find(group =>
    !!group && group.toLowerCase().includes("rnd_admin")
    && group.split('_').length === 5)
    || roles.find(role => role.toLowerCase().includes('rnd_admin') && role.split('_').length === 5)

  permissions.checkLLDivisionAdmin = !!llDivAdmin && {
    isAdmin: true,
    divisionCode: llDivAdmin.split('_')[2],
    divisionTitle: llDivAdmin.split('_')[2]
  } || {
    isAdmin: false,
    divisionCode: "",
    divisionTitle: ""
  }
  permissions.checkRnDDivisionAdmin = !!rndDivAdmin && {
    isAdmin: true,
    divisionCode: rndDivAdmin.split('_')[2],
    divisionTitle: rndDivAdmin.split('_')[2]
  } || {
    isAdmin: false,
    divisionCode: "",
    divisionTitle: ""
  }

  permissions.isAdmin =
    permissions.isSuperAdmin ||
    permissions.checkRnDDivisionAdmin?.isAdmin ||
    permissions.checkLLDivisionAdmin?.isAdmin ||
    permissions.isLLAdmin ||
    permissions.isThesisAdmin ||
    permissions.isPaperAdmin;
  permissions.checkHasPermission = (
    permissionList: string[]
  ): boolean => {
    return checkHasPermission(permissionList, permissions);
  };
  return permissions;
}

function checkHasPermission(
  permissionList: string[],
  permissions: IUserPermissions
): boolean {
  let hasPermission = false;
  permissionList.forEach((permission: string) => {
    switch (permission) {
      case Constants.PERMISSIONS.ADMIN: {
        hasPermission =
          hasPermission ||
          (!_.isUndefined(permissions.isAdmin) ? permissions.isAdmin : false);
        break;
      }
      case Constants.PERMISSIONS.SUPER_ADMIN: {
        hasPermission =
          hasPermission ||
          (!_.isUndefined(permissions.isSuperAdmin)
            ? permissions.isSuperAdmin
            : false);
        break;
      }
      case Constants.PERMISSIONS.RND_DIVISION_ADMIN: {
        hasPermission =
          hasPermission ||
          !!permissions.checkRnDDivisionAdmin && permissions.checkRnDDivisionAdmin.isAdmin || false;
        break;
      }
      case Constants.PERMISSIONS.LL_DIVISION_ADMIN: {
        hasPermission =
          hasPermission ||
          !!permissions.checkLLDivisionAdmin && permissions.checkLLDivisionAdmin.isAdmin || false;
        break;
      }
      case Constants.PERMISSIONS.LL_ADMIN: {
        hasPermission =
          hasPermission ||
          (!_.isUndefined(permissions.isLLAdmin)
            ? permissions.isLLAdmin
            : false);
        break;
      }
      case Constants.PERMISSIONS.THESIS_ADMIN: {
        hasPermission =
          hasPermission ||
          (!_.isUndefined(permissions.isThesisAdmin)
            ? permissions.isThesisAdmin
            : false);
        break;
      }
      case Constants.PERMISSIONS.PAPER_ADMIN: {
        hasPermission =
          hasPermission ||
          (!_.isUndefined(permissions.isPaperAdmin)
            ? permissions.isPaperAdmin
            : false);
        break;
      }
      case Constants.PERMISSIONS.RND_USER: {
        hasPermission =
          hasPermission ||
          (!_.isUndefined(permissions.isRnDUser)
            ? permissions.isRnDUser
            : false);
        break;
      }
      case Constants.PERMISSIONS.CONFIGURATION: {
        hasPermission =
          hasPermission ||
          (!_.isUndefined(permissions.isConfigurationUser)
            ? permissions.isConfigurationUser
            : false);
        break;
      }
      default: {
        break;
      }
    }
  });
  return hasPermission;
}

export const getRoles = async (userId: string, onlyFromDb: boolean = true) => {
  try {
    let myRoles = await (await FeberApiClient.get("Role/GetRoles", {
      params: {
        myId: removeAliasDomain(userId),
        onlyFromDb: onlyFromDb
      }
    }))
    return !!myRoles && myRoles.data.success && myRoles.data.data || []
  } catch (error) {
    console.log(error);
    return [];
  }
}

export const addSignInLog = async (record: ICustomerVisit) => {
  const response = await FeberApiClient.post("Log/CustomerVisit", record);
  if (response.data.success) {
    return response.data.data;
  } else {
    throw new Error(response.data.message);
  }
}